/**
 * Mer de la Tranquillité (GDD section 10), drawn at 1024 × 512: the Moon, where there is nothing. Moon rock under
 * a layer of gravel (the regolith), a few boulders and craters, and frozen seas at both ends (water under a crust
 * of ice). Rules (MapState.airless, gravityDiv): gravity divided by 3, no atmosphere (no wind, fire goes out at
 * once, steam freezes, the Wave freezes after 2 s), and a unit thrown above the top of the map is lost in space.
 * The seed changes nothing here.
 */
import { Physique, sinDeg, TRIG_ONE } from '@invaders/sim';
import { plainMap, type MapState } from '../state';
import { type Gen, ellipse, island, layer, rect, sea, settle } from './common';

const { ROCK, WATER, ICE, GRAVEL } = Physique;

/** Craters: [centre, radius]. Boulders: [x, rx, ry]. */
const CRATERS: readonly [number, number][] = [[226, 30], [330, 18], [452, 58], [598, 24], [706, 42], [808, 20]];
const BOULDERS: readonly [number, number, number][] = [[186, 8, 6], [272, 5, 4], [388, 11, 8], [540, 7, 5], [556, 4, 3], [652, 9, 7], [770, 6, 5], [842, 10, 7]];
/** Thickness of the ice crust over the frozen seas. */
const CRUST = 5;

export function drawMoon(g: Gen): MapState {
  const { seaY } = g;
  const coastL = 150;
  const coastR = 874;
  const relief = (x: number) => 64 + Math.trunc((18 * sinDeg((x >> 1) + 30)) / TRIG_ONE) + Math.trunc((7 * sinDeg(x * 2 + 70)) / TRIG_ONE) + Math.trunc((2 * sinDeg(x * 9)) / TRIG_ONE);
  const elevation = (x: number) => {
    let e = relief(x);
    for (const [cx, r] of CRATERS) {
      const d = Math.abs(x - cx);
      const depth = Math.trunc((r * 3) / 5);
      const rim = Math.trunc(r / 4) + 3;
      if (d < r) e -= Math.trunc((depth * (r * r - d * d)) / (r * r)) - rim;
      else if (d < r + (r >> 1)) e += Math.trunc((rim * (r + (r >> 1) - d)) / (r >> 1));
    }
    return e;
  };
  const surface = island(g, coastL, coastR, 56, elevation);

  // The regolith, then boulders half sunk in it.
  layer(g, surface, coastL - 40, coastR + 40, (x) => {
    const c = Math.min(x - coastL, coastR - x);
    return c < 0 ? Math.max(0, 12 + Math.trunc(c / 4)) : 12 + Math.trunc((3 * sinDeg(x * 5)) / TRIG_ONE);
  }, GRAVEL);
  for (const [x, rx, ry] of BOULDERS) ellipse(g, x, surface[x] + (ry >> 1), rx, ry, ROCK);

  // The frozen seas: the water, then its crust of ice.
  sea(g);
  for (let x = 0; x < g.w; x++) {
    if (g.data[seaY * g.w + x] === WATER) rect(g, x, seaY, x, seaY + CRUST - 1, ICE, WATER);
  }
  settle(g);
  return { ...plainMap('moon'), gravityDiv: 3, airless: true };
}
